import { refreshAppearance } from "./appearance";
/**
 * The front door.
 *
 * The form is the drawing's: a complex code, a unit or a staff id, and a
 * password, with the two doors as tabs. What is behind it is the app's real
 * sign-in, which is why the fields did not have to move: the phone asks for
 * exactly these three things and builds the same synthesised address from
 * them.
 *
 * A refusal is reported in the page's own error box rather than as a Firebase
 * message. "auth/invalid-credential" tells somebody nothing about which of the
 * three fields they got wrong, and telling them which one is a way of finding
 * out whether a unit exists, so it says the same sentence for all three.
 */

import { FirebaseError } from "firebase/app";
import { IdentityError, contactName, contactPhone, passwordProblems } from "@liyu/core";
import {
  NoAccountError,
  applyNewPassword,
  confirmResetCode,
  currentSession,
  sendResetCode,
  ResidentSetupError,
  confirmSetupCode,
  finishResidentSetup,
  finishStaffSetup,
  sendSetupCode,
  SetupRequiredError,
  signIn,
  WrongPortalError,
} from "./session";
import type { Portal } from "./session";
import { el, translate } from "./dom";
import { showLoader, showLoaderIfSlow } from "./loader";
import { afterSignIn } from "./routes";
import { holdLanguage, t } from "./strings";

// The sign-in page comes in English only. The theme button was labelled when its module
// loaded, before this line, so it is labelled again.
holdLanguage("en");
refreshAppearance();

type StaffRole = "committee" | "accountant";

let portal: Portal = "resident";
let staffRole: StaffRole = "committee";

const org = el<HTMLInputElement>("org");
const identity = el<HTMLInputElement>("identity");
const password = el<HTMLInputElement>("password");
const error = el("loginError");
const button = el<HTMLButtonElement>("loginButton");
const passwordToggle = el<HTMLButtonElement>("passwordToggle");

function paintPasswordVisibility(): void {
  const visible = password.type === "text";
  passwordToggle.setAttribute("aria-pressed", String(visible));
  passwordToggle.setAttribute(
    "aria-label",
    t(visible ? "signIn.hidePassword" : "signIn.showPassword"),
  );
  passwordToggle.querySelector("span")!.textContent = t(
    visible ? "signIn.hidePassword" : "signIn.showPassword",
  );
}

function paintMode(): void {
  const resident = el("residentMode");
  const staff = el("staffMode");
  const picker = el("staffRolePicker");
  resident.setAttribute("aria-pressed", String(portal === "resident"));
  staff.setAttribute("aria-pressed", String(portal === "staff"));

  if (portal === "resident") {
    resident.className = "tap flex-1 rounded-lg font-bold tab-active";
    resident.style.color = "";
    staff.className = "tap flex-1 rounded-lg font-bold";
    staff.style.color = "var(--muted)";
  } else {
    resident.className = "tap flex-1 rounded-lg font-bold";
    resident.style.color = "var(--muted)";
    staff.className = "tap flex-1 rounded-lg font-bold tab-active";
    staff.style.color = "";
  }
  picker.classList.toggle("hidden", portal !== "staff");

  el("identityLabel").textContent = t(portal === "resident" ? "signIn.unit" : "signIn.staffId");
  identity.placeholder = t(portal === "resident" ? "signIn.unitHint" : "signIn.staffIdHint");
  org.placeholder = t("signIn.orgCodeHint");
  password.placeholder = t("signIn.passwordHint");
  error.classList.add("hidden");
}

function paintStaffRole(): void {
  const committee = el("committeeRole");
  const accountant = el("accountantRole");
  committee.setAttribute("aria-pressed", String(staffRole === "committee"));
  accountant.setAttribute("aria-pressed", String(staffRole === "accountant"));
  const on = "tap rounded-lg border-2 font-bold";
  const off = "tap rounded-lg border-2 border-[#BAD2E7] font-bold";
  const chosen = "border-color:var(--brown);background:var(--brown);color:var(--gold-bright)";

  committee.className = staffRole === "committee" ? on : off;
  committee.style.cssText = staffRole === "committee" ? chosen : "color:var(--muted)";
  accountant.className = staffRole === "accountant" ? on : off;
  accountant.style.cssText = staffRole === "accountant" ? chosen : "color:var(--muted)";
}

/**
 * What went wrong, in words that do not describe the account.
 *
 * The one exception is the wrong door, which is worth naming: somebody at the
 * staff entrance with a resident's account has an account, and sending them to
 * the other tab is the whole of the fix.
 */
function reasonFor(cause: unknown): string {
  if (cause instanceof WrongPortalError) {
    return t("signIn.wrongDoor");
  }
  if (cause instanceof IdentityError) {
    return t("signIn.refused");
  }
  if (cause instanceof FirebaseError) {
    if (cause.code === "auth/too-many-requests") {
      return t("signIn.tooMany");
    }
    if (cause.code === "auth/network-request-failed") {
      return t("signIn.offline");
    }
    return t("signIn.refused");
  }
  throw cause;
}

function wire(): void {
  el("residentMode").addEventListener("click", () => {
    portal = "resident";
    identity.value = "";
    password.value = "";
    paintMode();
  });
  el("staffMode").addEventListener("click", () => {
    portal = "staff";
    identity.value = "";
    password.value = "";
    paintMode();
    paintStaffRole();
  });
  el("committeeRole").addEventListener("click", () => {
    staffRole = "committee";
    paintStaffRole();
  });
  el("accountantRole").addEventListener("click", () => {
    staffRole = "accountant";
    paintStaffRole();
  });

  el<HTMLFormElement>("loginForm").addEventListener("submit", (event) => {
    event.preventDefault();
    void attempt();
  });

  passwordToggle.addEventListener("click", () => {
    password.type = password.type === "password" ? "text" : "password";
    paintPasswordVisibility();
    password.focus();
  });
}

async function attempt(): Promise<void> {
  error.classList.add("hidden");
  button.disabled = true;
  button.textContent = t("signIn.working");
  // The logo covers the wait; the app page carries it on until its first screen is drawn.
  const loader = showLoader(t("signIn.working"));
  try {
    await signIn(org.value, identity.value, password.value, portal);
    window.location.href = afterSignIn(window.location.search);
  } catch (cause) {
    loader.hide();
    if (cause instanceof SetupRequiredError) {
      el("loginForm").classList.add("hidden");
      el("setupForm").classList.remove("hidden");
      el<HTMLInputElement>("newPassword").focus();
      return;
    }
    if (cause instanceof ResidentSetupError) {
      startResidentSetup(cause);
      return;
    }
    error.textContent = reasonFor(cause);
    error.classList.remove("hidden");
    button.disabled = false;
    button.textContent = t("signIn.submit");
  }
}

/* ------------------------------------------------ a resident's first login */

/**
 * Set when the person choosing a password is a resident: they confirm a phone
 * first, by text message unless the office vouched for them, and give a name.
 */
let residentSetup: { vouched: boolean; phoneConfirmed: boolean } | null = null;

function setupProblem(message: string | null): void {
  const box = el("setupError");
  box.textContent = message ?? "";
  box.classList.toggle("hidden", message === null);
}

function paintResidentSetup(): void {
  if (!residentSetup) return;
  const ready = residentSetup.vouched || residentSetup.phoneConfirmed;
  el("setupDetails").classList.remove("hidden");
  el("setupPasswordStep").classList.toggle("hidden", !ready);
  el("setupSendCode").classList.toggle("hidden", ready);
  el("setupDetailsLead").textContent = t(
    residentSetup.vouched ? "signIn.residentSetupVouched" : "signIn.residentSetupLead",
  );
  el<HTMLInputElement>("setupPhone").readOnly = residentSetup.phoneConfirmed;
}

function startResidentSetup(found: ResidentSetupError): void {
  residentSetup = { vouched: found.vouched, phoneConfirmed: found.confirmedPhone !== null };
  if (found.confirmedPhone) {
    el<HTMLInputElement>("setupPhone").value = found.confirmedPhone;
  }
  el("loginForm").classList.add("hidden");
  el("setupForm").classList.remove("hidden");
  paintResidentSetup();
  el<HTMLInputElement>("setupName").focus();
}

function typedPhone(): string | null {
  try {
    return contactPhone(el<HTMLInputElement>("setupPhone").value);
  } catch {
    setupProblem(t("reset.badPhone"));
    return null;
  }
}

function setupCodeRefusal(cause: unknown): string {
  if (cause instanceof FirebaseError) {
    if (
      cause.code === "auth/credential-already-in-use" ||
      cause.code === "auth/account-exists-with-different-credential"
    ) {
      return t("signIn.setupPhoneTaken");
    }
    if (cause.code === "auth/invalid-verification-code" || cause.code === "auth/code-expired") {
      return t("reset.badCode");
    }
  }
  return reasonFor(cause);
}

async function setUpResident(chosen: string): Promise<void> {
  let name: string;
  try {
    name = contactName(el<HTMLInputElement>("setupName").value);
  } catch {
    setupProblem(t("signIn.setupNameNeeded"));
    return;
  }
  const phone = typedPhone();
  if (phone === null) return;
  const save = el<HTMLButtonElement>("setupButton");
  save.disabled = true;
  save.textContent = t("signIn.setupWorking");
  try {
    await finishResidentSetup({ name, phone, password: chosen });
    window.location.href = afterSignIn(window.location.search);
  } catch (cause) {
    setupProblem(reasonFor(cause));
    save.disabled = false;
    save.textContent = t("signIn.setupSubmit");
  }
}

async function setUp(): Promise<void> {
  const chosen = el<HTMLInputElement>("newPassword").value;
  const again = el<HTMLInputElement>("newPasswordAgain").value;
  const problem = el("setupError");
  const save = el<HTMLButtonElement>("setupButton");
  const [first] = passwordProblems(chosen);
  const refusal = first
    ? t(first === "tooShort" ? "signIn.setupTooShort" : "signIn.setupNeedsLetterAndNumber")
    : chosen !== again
      ? t("signIn.setupMismatch")
      : null;
  if (refusal) {
    problem.textContent = refusal;
    problem.classList.remove("hidden");
    return;
  }
  problem.classList.add("hidden");
  if (residentSetup) {
    await setUpResident(chosen);
    return;
  }
  save.disabled = true;
  save.textContent = t("signIn.setupWorking");
  try {
    await finishStaffSetup(chosen);
    window.location.href = afterSignIn(window.location.search);
  } catch (cause) {
    problem.textContent = reasonFor(cause);
    problem.classList.remove("hidden");
    save.disabled = false;
    save.textContent = t("signIn.setupSubmit");
  }
}

/* ------------------------------------------------------ forgotten password */

function resetProblem(message: string | null): void {
  const box = el("resetError");
  box.textContent = message ?? "";
  box.classList.toggle("hidden", message === null);
}

function resetStep(step: "phone" | "code" | "password"): void {
  el("resetPhoneStep").classList.toggle("hidden", step !== "phone");
  el("resetCodeStep").classList.toggle("hidden", step !== "code");
  el("resetPasswordStep").classList.toggle("hidden", step !== "password");
}

el("forgotLink").addEventListener("click", () => {
  el("loginForm").classList.add("hidden");
  el("resetForm").classList.remove("hidden");
  resetStep("phone");
  resetProblem(null);
  el<HTMLInputElement>("resetPhone").focus();
});
el("resetBack").addEventListener("click", () => {
  el("resetForm").classList.add("hidden");
  el("loginForm").classList.remove("hidden");
});
el("resetSend").addEventListener("click", () => {
  let phone: string;
  try {
    phone = contactPhone(el<HTMLInputElement>("resetPhone").value);
  } catch {
    resetProblem(t("reset.badPhone"));
    return;
  }
  resetProblem(null);
  void sendResetCode(phone, "resetCaptcha").then(
    () => {
      resetStep("code");
      el<HTMLInputElement>("resetCode").focus();
    },
    (cause: unknown) => resetProblem(reasonFor(cause)),
  );
});
el("resetVerify").addEventListener("click", () => {
  void confirmResetCode(el<HTMLInputElement>("resetCode").value).then(
    () => {
      resetProblem(null);
      resetStep("password");
      el<HTMLInputElement>("resetPassword").focus();
    },
    (cause: unknown) =>
      resetProblem(cause instanceof NoAccountError ? t("reset.noAccount") : t("reset.badCode")),
  );
});
el("resetSave").addEventListener("click", () => {
  const chosen = el<HTMLInputElement>("resetPassword").value;
  const again = el<HTMLInputElement>("resetPasswordAgain").value;
  const [first] = passwordProblems(chosen);
  if (first) {
    resetProblem(
      t(first === "tooShort" ? "signIn.setupTooShort" : "signIn.setupNeedsLetterAndNumber"),
    );
    return;
  }
  if (chosen !== again) {
    resetProblem(t("signIn.setupMismatch"));
    return;
  }
  void applyNewPassword(chosen).then(
    () => {
      resetProblem(null);
      window.location.href = afterSignIn(window.location.search);
    },
    (cause: unknown) => resetProblem(reasonFor(cause)),
  );
});

el("setupSendCode").addEventListener("click", () => {
  setupProblem(null);
  const phone = typedPhone();
  if (phone === null) return;
  const send = el<HTMLButtonElement>("setupSendCode");
  send.disabled = true;
  void sendSetupCode(phone, "setupCaptcha")
    .then(
      () => {
        el<HTMLInputElement>("setupPhone").value = phone;
        el("setupCodeStep").classList.remove("hidden");
        el<HTMLInputElement>("setupCode").focus();
      },
      (cause: unknown) => setupProblem(setupCodeRefusal(cause)),
    )
    .finally(() => {
      send.disabled = false;
    });
});

el("setupVerify").addEventListener("click", () => {
  setupProblem(null);
  const verify = el<HTMLButtonElement>("setupVerify");
  verify.disabled = true;
  void confirmSetupCode(el<HTMLInputElement>("setupCode").value)
    .then(
      () => {
        if (residentSetup) residentSetup.phoneConfirmed = true;
        el("setupCodeStep").classList.add("hidden");
        paintResidentSetup();
        el<HTMLInputElement>("newPassword").focus();
      },
      (cause: unknown) => setupProblem(setupCodeRefusal(cause)),
    )
    .finally(() => {
      verify.disabled = false;
    });
});

el<HTMLFormElement>("setupForm").addEventListener("submit", (event) => {
  event.preventDefault();
  void setUp();
});

translate();
paintMode();
paintStaffRole();
paintPasswordVisibility();
wire();

/** Shows the form, once it is clear this person has to sign in. */
function showForm(): void {
  document.body.classList.remove("checking-session");
}

// Somebody already signed in who lands here goes straight through, to the
// screen their link asked for, without the form ever showing. A check that is
// slow (an expired token being refreshed on a poor connection) gives way to
// the form rather than holding the page blank.
// A quick answer never shows the logo; a slow one does, and an answer of "signed in"
// leaves it up for the app page to carry on.
const checking = showLoaderIfSlow(t("signIn.checking"));
const giveUp = window.setTimeout(() => {
  checking.hide();
  showForm();
}, 3000);
void currentSession().then(
  (session) => {
    if (session) {
      window.location.replace(afterSignIn(window.location.search));
      return;
    }
    window.clearTimeout(giveUp);
    checking.hide();
    showForm();
  },
  (cause: unknown) => {
    console.error("Could not tell whether this browser is signed in", cause);
    window.clearTimeout(giveUp);
    checking.hide();
    showForm();
  },
);
